/**
 * Feature-flagged real login flow against lsgo-authentication, triggered
 * from the app's own real route guard (routing/utils/useAuthCheck.tsx) —
 * NOT the LsgoAuthenticationCheck.js PoC page (which uses a fixed, publicly
 * -known PKCE pair and is only ever visited directly for a manual check).
 * This one generates a real per-attempt PKCE pair, so it's safe to use from
 * any real protected route while both flows stay intact side by side.
 *
 * Gated by a localStorage flag (`lsgo_new_auth_enabled`) rather than a
 * server-side config or env var — deliberately something you can flip from
 * the browser console on your own machine without a rebuild, to compare
 * both flows route by route before this becomes the only path.
 */

import {
    LSGO_AUTHENTICATION_BASE_URL,
    LSGO_AUTHENTICATION_CLIENT_ID,
    LSGO_AUTHENTICATION_REDIRECT_URI,
} from 'config/lsgoAuthentication';

// Per-environment values live in config/lsgoAuthentication.js.
export const AUTH_BASE_URL = LSGO_AUTHENTICATION_BASE_URL;
export const CLIENT_ID = LSGO_AUTHENTICATION_CLIENT_ID;
export const REDIRECT_URI = LSGO_AUTHENTICATION_REDIRECT_URI;

const FLAG_KEY = 'lsgo_new_auth_enabled';
const PENDING_KEY_PREFIX = 'lsgo_new_auth_pending:';

export function isNewAuthFlowEnabled() {
    try {
        return localStorage.getItem(FLAG_KEY) === 'true';
    } catch {
        return false;
    }
}

export function setNewAuthFlowEnabled(enabled) {
    localStorage.setItem(FLAG_KEY, enabled ? 'true' : 'false');
}

function base64url(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomBase64url(byteLength) {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return base64url(bytes);
}

export async function codeChallengeFor(verifier) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64url(new Uint8Array(digest));
}

/**
 * Where a sign-in may return to: only a plain same-origin path. Anything
 * else (absolute or protocol-relative URLs, backslashes browsers fold to
 * "/", a repeated query param) falls back to '/teacher'. The return path is
 * later handed to navigate() by LsgoAuthenticationCallback.
 */
export function safeReturnPath(raw) {
    const fallback = '/teacher';
    if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
        return fallback;
    }
    return raw;
}

/**
 * Starts a real OAuth2.1 + PKCE login against lsgo-authentication, ending
 * back at `returnPath` (this app's own path the user was actually trying to
 * reach) once LsgoAuthenticationCallback.js completes the exchange. The
 * verifier and the return path are stashed in sessionStorage keyed by a
 * fresh `state` nonce — `state` itself only ever needs to be an opaque
 * lookup key here (PKCE, not `state`, is this flow's actual CSRF/replay
 * defence — see lsgo-authentication's own docs/BUILD_PLAN.md), so nothing
 * about the return path is ever exposed in the URL/referrer.
 *
 * `idp` + `tenant` (SIS SSO links, last-SIS sign-in): go straight to that
 * school's provider instead of the sign-in page. `returnPath` goes through
 * safeReturnPath.
 */
export async function startLsgoAuthenticationLogin(returnPath, { idp, tenant } = {}) {
    const verifier = randomBase64url(32);
    const challenge = await codeChallengeFor(verifier);
    const state = randomBase64url(16);

    sessionStorage.setItem(
        `${PENDING_KEY_PREFIX}${state}`,
        JSON.stringify({ verifier, returnPath: safeReturnPath(returnPath) })
    );

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        state,
        scope: 'openid profile',
    });
    if (idp) params.set('idp', idp);
    if (tenant) params.set('tenant', tenant);
    window.location.href = `${AUTH_BASE_URL}/authorize?${params.toString()}`;
}

/**
 * Reads back and consumes (single-use) the verifier + return path stashed
 * by startLsgoAuthenticationLogin for a given `state`. Returns null if
 * there's no matching entry — a stale reload of the callback URL, a
 * tampered `state`, or a flow that didn't originate from this helper.
 */
export function consumePendingLogin(state) {
    const key = `${PENDING_KEY_PREFIX}${state}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}
